import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { ConsignmentSettlementService } from "services/consignment-settlement";
import { formatVndIntegerInput, parseVndIntegerInput } from "utils";
import { getPrintTemplate } from "./components/printTemplate";

const createRow = () => ({
  itemName: "",
  unit: "",
  quantityPrevious: "",
  quantityReceived: "",
  quantitySold: "",
  unitPrice: "",
});

const createRepresentative = () => ({
  name: "",
  position: "",
  representative: "",
});

const splitDateParts = (dateText = "") => {
  const [day = "", month = "", year = ""] = String(dateText).split("/");
  return {
    day: day.replace(/\D/g, "").slice(0, 2),
    month: month.replace(/\D/g, "").slice(0, 2),
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const hasAnyDatePart = (parts) => Boolean(parts.day || parts.month || parts.year);
const hasAllDatePart = (parts) => Boolean(parts.day && parts.month && parts.year);

const composeDate = (parts) => {
  if (!hasAnyDatePart(parts)) return "";
  const day = parts.day.padStart(2, "0");
  const month = parts.month.padStart(2, "0");
  const year = parts.year;
  return `${day}/${month}/${year}`;
};

const validateDateParts = (parts, label) => {
  if (!hasAnyDatePart(parts)) return "";
  if (!hasAllDatePart(parts)) {
    throw new Error(`${label} chưa nhập đủ ngày/tháng/năm`);
  }
  const day = Number(parts.day);
  const month = Number(parts.month);
  const year = Number(parts.year);
  if (month < 1 || month > 12) {
    throw new Error(`${label} có tháng không hợp lệ`);
  }
  if (year < 1900 || year > 9999) {
    throw new Error(`${label} có năm không hợp lệ`);
  }
  const maxDays = new Date(year, month, 0).getDate();
  if (day < 1 || day > maxDays) {
    throw new Error(`${label} có ngày không hợp lệ`);
  }
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${String(year)}`;
};

const parseQty = (value) => {
  const normalized = String(value || "").replace(/,/g, ".").replace(/[^\d.]/g, "");
  if (!normalized) return 0;
  const numeric = Number(normalized);
  if (!Number.isFinite(numeric) || numeric < 0) return 0;
  return Number(numeric.toFixed(3));
};

const formatQtyInput = (value) => String(value || "").replace(/,/g, ".").replace(/[^\d.]/g, "");

const formatQtyOutput = (value) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "";
  if (Math.abs(number - Math.trunc(number)) < 0.000001) return `${Math.trunc(number)}`;
  return number.toFixed(3).replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1");
};

const roundMoney = (value) => {
  const parsed = parseVndIntegerInput(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : 0;
};

const fmtMoney = (value) => new Intl.NumberFormat("vi-VN").format(Math.round(Number(value) || 0));

export default function ConsignmentSettlementDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const today = new Date();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [documentDateParts, setDocumentDateParts] = useState({
    day: String(today.getDate()).padStart(2, "0"),
    month: String(today.getMonth() + 1).padStart(2, "0"),
    year: String(today.getFullYear()),
  });
  const [contractDateParts, setContractDateParts] = useState(splitDateParts(""));
  const [fromDateParts, setFromDateParts] = useState(splitDateParts(""));
  const [toDateParts, setToDateParts] = useState(splitDateParts(""));

  const [header, setHeader] = useState({
    unit: "",
    address: "",
    bookNumber: "",
    documentNumber: "",
    debitAccount: "",
    creditAccount: "",
    contractNumber: "",
    note: "",
  });

  const [representatives, setRepresentatives] = useState([
    createRepresentative(),
    createRepresentative(),
    createRepresentative(),
    createRepresentative(),
  ]);

  const [rows, setRows] = useState([createRow()]);

  const [summary, setSummary] = useState({
    previousDebt: "",
    commission: "",
    taxPaid: "",
    otherExpense: "",
    paidCash: "",
    paidCheque: "",
  });

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await ConsignmentSettlementService.getById(id);
        const data = response.data ? response.data : response;

        setHeader({
          unit: data.unit || "",
          address: data.address || "",
          bookNumber: data.bookNumber || "",
          documentNumber: data.documentNumber || "",
          debitAccount: data.debitAccount || "",
          creditAccount: data.creditAccount || "",
          contractNumber: data.contractNumber || "",
          note: data.note || "",
        });

        setDocumentDateParts(splitDateParts(data.documentDate || ""));
        setContractDateParts(splitDateParts(data.contractDate || ""));
        setFromDateParts(splitDateParts(data.fromDate || ""));
        setToDateParts(splitDateParts(data.toDate || ""));

        const mappedReps = (data.representatives || []).map((rep) => ({
          name: rep?.name || "",
          position: rep?.position || "",
          representative: rep?.representative || "",
        }));
        while (mappedReps.length < 4) mappedReps.push(createRepresentative());
        setRepresentatives(mappedReps.slice(0, 4));

        const mappedRows = (data.rows || []).map((row) => ({
          itemName: row.itemName || "",
          unit: row.unit || "",
          quantityPrevious: formatQtyOutput(row.quantityPrevious),
          quantityReceived: formatQtyOutput(row.quantityReceived),
          quantitySold: formatQtyOutput(row.quantitySold),
          unitPrice: formatVndIntegerInput(row.unitPrice || ""),
        }));

        setRows(mappedRows.length ? mappedRows : [createRow()]);

        setSummary({
          previousDebt: formatVndIntegerInput(data.previousDebt || ""),
          commission: formatVndIntegerInput(data.commission || ""),
          taxPaid: formatVndIntegerInput(data.taxPaid || ""),
          otherExpense: formatVndIntegerInput(data.otherExpense || ""),
          paidCash: formatVndIntegerInput(data.paidCash || ""),
          paidCheque: formatVndIntegerInput(data.paidCheque || ""),
        });
      } catch (error) {
        toast.error("Không thể tải dữ liệu bảng thanh toán hàng đại lý");
      }
    })();
  }, [id]);

  const derivedRows = useMemo(() =>
    rows.map((row) => {
      const quantityPrevious = parseQty(row.quantityPrevious);
      const quantityReceived = parseQty(row.quantityReceived);
      const quantitySold = parseQty(row.quantitySold);
      const quantityTotal = quantityPrevious + quantityReceived;
      const unitPrice = roundMoney(row.unitPrice);
      const amount = Math.round(quantitySold * unitPrice);
      const quantityRemaining = quantityTotal - quantitySold;

      return {
        ...row,
        quantityPrevious,
        quantityReceived,
        quantityTotal,
        quantitySold,
        unitPrice,
        amount,
        quantityRemaining,
      };
    }),
  [rows]);

  const totalAmount = useMemo(
    () => derivedRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [derivedRows]
  );

  const summaryNumbers = useMemo(() => {
    const previousDebt = roundMoney(summary.previousDebt);
    const commission = roundMoney(summary.commission);
    const taxPaid = roundMoney(summary.taxPaid);
    const otherExpense = roundMoney(summary.otherExpense);
    const payableToConsignee = commission + taxPaid + otherExpense;
    const paidCash = roundMoney(summary.paidCash);
    const paidCheque = roundMoney(summary.paidCheque);
    const payableNew = previousDebt + totalAmount;
    const totalPaid = paidCash + paidCheque;
    const remainingDebt = payableNew - payableToConsignee - totalPaid;

    return {
      previousDebt,
      commission,
      taxPaid,
      otherExpense,
      payableToConsignee,
      paidCash,
      paidCheque,
      payableNew,
      totalPaid,
      remainingDebt,
    };
  }, [summary, totalAmount]);

  const setHeaderField = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const setDateField = (setter) => (field, value) => {
    const max = field === "year" ? 4 : 2;
    const raw = value.replace(/\D/g, "");
    setter((prev) => ({ ...prev, [field]: field === "year" ? raw.slice(0, max) : raw.slice(-max) }));
  };

  const padDateField = (setter) => (field) => {
    if (field === "year") return;
    setter((prev) => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: String(prev[field]).padStart(2, "0") };
    });
  };

  const setRowField = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      let nextValue = value;
      if (["quantityPrevious", "quantityReceived", "quantitySold"].includes(field)) {
        nextValue = formatQtyInput(value);
      }
      if (field === "unitPrice") {
        nextValue = formatVndIntegerInput(value);
      }
      next[index] = { ...next[index], [field]: nextValue };
      return next;
    });
  };

  const setRepresentativeField = (index, field, value) => {
    setRepresentatives((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const setSummaryField = (field, value) => {
    setSummary((prev) => ({
      ...prev,
      [field]: formatVndIntegerInput(value),
    }));
  };

  const addRow = () => {
    setRows((prev) => [...prev, createRow()]);
  };

  const removeRow = (index) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const buildPayload = () => {
    const unit = String(header.unit || "").trim();
    const address = String(header.address || "").trim();
    const documentDate = validateDateParts(documentDateParts, "Ngày lập bảng");
    const bookNumber = String(header.bookNumber || "").trim();
    const documentNumber = String(header.documentNumber || "").trim();
    const debitAccount = String(header.debitAccount || "").trim();
    const creditAccount = String(header.creditAccount || "").trim();
    const contractNumber = String(header.contractNumber || "").trim();
    const note = String(header.note || "").trim();
    const contractDate = validateDateParts(contractDateParts, "Ngày hợp đồng");
    const fromDate = validateDateParts(fromDateParts, "Từ ngày");
    const toDate = validateDateParts(toDateParts, "Đến ngày");

    if (!unit) throw new Error("Đơn vị không được để trống");
    if (!address) throw new Error("Địa chỉ không được để trống");
    if (!documentDate) throw new Error("Ngày lập bảng không được để trống");
    if (!documentNumber) throw new Error("Số bảng thanh toán không được để trống");
    if (!contractNumber) throw new Error("Số hợp đồng không được để trống");
    if (!contractDate) throw new Error("Ngày hợp đồng không được để trống");
    if (!fromDate || !toDate) throw new Error("Từ ngày/Đến ngày không được để trống");

    const normalizedRows = derivedRows
      .map((row, index) => ({
        serialNumber: String(index + 1),
        itemName: String(row.itemName || "").trim(),
        unit: String(row.unit || "").trim(),
        quantityPrevious: row.quantityPrevious,
        quantityReceived: row.quantityReceived,
        quantityTotal: row.quantityTotal,
        quantitySold: row.quantitySold,
        unitPrice: row.unitPrice,
        amount: row.amount,
        quantityRemaining: row.quantityRemaining,
      }))
      .filter((row) => {
        const hasQty =
          row.quantityPrevious > 0 ||
          row.quantityReceived > 0 ||
          row.quantitySold > 0 ||
          row.unitPrice > 0 ||
          row.amount > 0;
        return row.itemName || row.unit || hasQty;
      });

    if (!normalizedRows.length) {
      throw new Error("Bảng thanh toán phải có ít nhất 1 dòng dữ liệu");
    }

    normalizedRows.forEach((row, index) => {
      if (!row.itemName) {
        throw new Error(`Dòng ${index + 1}: Tên, quy cách, phẩm chất không được để trống`);
      }
      if (row.quantitySold > row.quantityTotal) {
        throw new Error(`Dòng ${index + 1}: Số lượng đã bán không được lớn hơn tổng số lượng`);
      }
    });

    const reps = representatives.map((rep) => ({
      name: String(rep.name || "").trim(),
      position: String(rep.position || "").trim(),
      representative: String(rep.representative || "").trim(),
    }));

    const previousDebt = summaryNumbers.previousDebt;
    const commission = summaryNumbers.commission;
    const taxPaid = summaryNumbers.taxPaid;
    const otherExpense = summaryNumbers.otherExpense;
    const payableToConsignee = summaryNumbers.payableToConsignee;
    const paidCash = summaryNumbers.paidCash;
    const paidCheque = summaryNumbers.paidCheque;
    const payableNew = summaryNumbers.payableNew;
    const totalPaid = summaryNumbers.totalPaid;
    const remainingDebt = summaryNumbers.remainingDebt;

    if (remainingDebt < 0) {
      throw new Error("Số tiền còn nợ không hợp lệ");
    }

    return {
      unit,
      address,
      documentDate,
      bookNumber,
      documentNumber,
      debitAccount,
      creditAccount,
      contractNumber,
      note,
      contractDate,
      fromDate,
      toDate,
      representatives: reps,
      rows: normalizedRows,
      previousDebt,
      payableNew,
      commission,
      taxPaid,
      otherExpense,
      payableToConsignee,
      paidCash,
      paidCheque,
      totalPaid,
      remainingDebt,
      totalAmount,
    };
  };

  const submit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await ConsignmentSettlementService.create(payload);
        toast.success("Tạo bảng thanh toán hàng đại lý thành công");
      } else {
        await ConsignmentSettlementService.update(id, payload);
        toast.success("Cập nhật bảng thanh toán hàng đại lý thành công");
      }
      navigation("/templates/consignment-settlement");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu thất bại");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const htmlContent = getPrintTemplate({
      headerInfo: {
        ...header,
        documentDate: composeDate(documentDateParts),
        contractDate: composeDate(contractDateParts),
        fromDate: composeDate(fromDateParts),
        toDate: composeDate(toDateParts),
      },
      representatives,
      rows: derivedRows.map((row, index) => ({ ...row, serialNumber: String(index + 1) })),
      totalAmount,
      summary: summaryNumbers,
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "16px",
    },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      minWidth: 20,
    },
    inputDate: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      width: 36,
      textAlign: "center",
    },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 1200 },
    th: { border: "1px solid #000", padding: 4, textAlign: "center", fontWeight: "bold", verticalAlign: "middle" },
    td: { border: "1px solid #000", padding: 0, height: 34 },
    inputLeft: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: 14,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    inputCenter: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "center",
      fontSize: 14,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    inputRight: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: 14,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox p={2} style={s.container}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ lineHeight: 1.5 }}>
                    <div>
                      <b>Đơn vị:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.unit}
                        onChange={(event) => setHeaderField("unit", event.target.value)}
                      />
                    </div>
                    <div>
                      <b>Địa chỉ:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.address}
                        onChange={(event) => setHeaderField("address", event.target.value)}
                      />
                    </div>
                  </div>

                  <div style={{ textAlign: "center", fontSize: 15, lineHeight: 1.35 }}>
                    <div>
                      <b>Mẫu số: 01 - BH</b>
                    </div>
                    <div>
                      <i>(Kèm theo Thông tư số 99/2025/TT-BTC</i>
                    </div>
                    <div>
                      <i>ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</i>
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: "center", marginTop: 6 }}>
                  <div style={{ fontWeight: 700, fontSize: 26, textTransform: "uppercase" }}>
                    BẢNG THANH TOÁN HÀNG ĐẠI LÝ, KÝ GỬI
                  </div>
                  <div style={{ marginTop: 8, fontStyle: "italic" }}>
                    Ngày{" "}
                    <input
                      disabled={containView}
                      style={s.inputDate}
                      value={documentDateParts.day}
                      onChange={(event) => setDateField(setDocumentDateParts)("day", event.target.value)}
                      onBlur={() => padDateField(setDocumentDateParts)("day")}
                    />
                    {" "}tháng{" "}
                    <input
                      disabled={containView}
                      style={s.inputDate}
                      value={documentDateParts.month}
                      onChange={(event) => setDateField(setDocumentDateParts)("month", event.target.value)}
                      onBlur={() => padDateField(setDocumentDateParts)("month")}
                    />
                    {" "}năm{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDate, width: 56 }}
                      value={documentDateParts.year}
                      onChange={(event) => setDateField(setDocumentDateParts)("year", event.target.value)}
                    />
                  </div>
                </div>

                <div style={{ textAlign: "right", marginTop: 8, lineHeight: 1.7 }}>
                  <div>
                    Quyển số:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 120, textAlign: "center" }}
                      value={header.bookNumber}
                      onChange={(event) => setHeaderField("bookNumber", event.target.value)}
                    />
                  </div>
                  <div>
                    Số:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 120, textAlign: "center" }}
                      value={header.documentNumber}
                      onChange={(event) => setHeaderField("documentNumber", event.target.value)}
                    />
                  </div>
                  <div>
                    Nợ:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 120, textAlign: "center" }}
                      value={header.debitAccount}
                      onChange={(event) => setHeaderField("debitAccount", event.target.value)}
                    />
                  </div>
                  <div>
                    Có:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 120, textAlign: "center" }}
                      value={header.creditAccount}
                      onChange={(event) => setHeaderField("creditAccount", event.target.value)}
                    />
                  </div>
                </div>

                <div style={{ marginTop: 10, lineHeight: 1.6 }}>
                  Căn cứ Hợp đồng số:{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 140 }}
                    value={header.contractNumber}
                    onChange={(event) => setHeaderField("contractNumber", event.target.value)}
                  />
                  {" "}ngày{" "}
                  <input
                    disabled={containView}
                    style={s.inputDate}
                    value={contractDateParts.day}
                    onChange={(event) => setDateField(setContractDateParts)("day", event.target.value)}
                    onBlur={() => padDateField(setContractDateParts)("day")}
                  />
                  {" "}tháng{" "}
                  <input
                    disabled={containView}
                    style={s.inputDate}
                    value={contractDateParts.month}
                    onChange={(event) => setDateField(setContractDateParts)("month", event.target.value)}
                    onBlur={() => padDateField(setContractDateParts)("month")}
                  />
                  {" "}năm{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDate, width: 56 }}
                    value={contractDateParts.year}
                    onChange={(event) => setDateField(setContractDateParts)("year", event.target.value)}
                  />
                  {" "}về việc bán hàng đại lý (ký gửi),
                </div>

                <div style={{ marginTop: 8, lineHeight: 1.7 }}>
                  <div><b>Chúng tôi gồm:</b></div>
                  {[0, 1, 2, 3].map((idx) => (
                    <div key={`rep-${idx}`}>
                      - Ông/Bà{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 180 }}
                        value={representatives[idx]?.name || ""}
                        onChange={(event) => setRepresentativeField(idx, "name", event.target.value)}
                      />
                      {" "}chức vụ{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 140 }}
                        value={representatives[idx]?.position || ""}
                        onChange={(event) => setRepresentativeField(idx, "position", event.target.value)}
                      />
                      {" "}Đại diện{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={representatives[idx]?.representative || ""}
                        onChange={(event) => setRepresentativeField(idx, "representative", event.target.value)}
                      />
                      {idx === 0 ? " có hàng đại lý (ký gửi)." : idx === 2 ? " nhận bán hàng đại lý (ký gửi)." : "."}
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: 12 }}>
                  <b>I- Thanh quyết toán số hàng đại lý từ ngày</b>{" "}
                  <input
                    disabled={containView}
                    style={s.inputDate}
                    value={fromDateParts.day}
                    onChange={(event) => setDateField(setFromDateParts)("day", event.target.value)}
                    onBlur={() => padDateField(setFromDateParts)("day")}
                  />
                  {" / "}
                  <input
                    disabled={containView}
                    style={s.inputDate}
                    value={fromDateParts.month}
                    onChange={(event) => setDateField(setFromDateParts)("month", event.target.value)}
                    onBlur={() => padDateField(setFromDateParts)("month")}
                  />
                  {" / "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDate, width: 56 }}
                    value={fromDateParts.year}
                    onChange={(event) => setDateField(setFromDateParts)("year", event.target.value)}
                  />
                  {" "}đến ngày{" "}
                  <input
                    disabled={containView}
                    style={s.inputDate}
                    value={toDateParts.day}
                    onChange={(event) => setDateField(setToDateParts)("day", event.target.value)}
                    onBlur={() => padDateField(setToDateParts)("day")}
                  />
                  {" / "}
                  <input
                    disabled={containView}
                    style={s.inputDate}
                    value={toDateParts.month}
                    onChange={(event) => setDateField(setToDateParts)("month", event.target.value)}
                    onBlur={() => padDateField(setToDateParts)("month")}
                  />
                  {" / "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDate, width: 56 }}
                    value={toDateParts.year}
                    onChange={(event) => setDateField(setToDateParts)("year", event.target.value)}
                  />
                  {" "}như sau:
                </div>

                <div style={{ overflowX: "auto", border: "1px solid #ccc", marginTop: 10 }}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th rowSpan={2} style={{ ...s.th, width: 50 }}>Số TT</th>
                        <th rowSpan={2} style={{ ...s.th, width: 280 }}>Tên, quy cách, phẩm chất sản phẩm (hàng hóa)</th>
                        <th rowSpan={2} style={{ ...s.th, width: 90 }}>Đơn vị tính</th>
                        <th rowSpan={2} style={{ ...s.th, width: 90 }}>Số lượng tồn đầu kỳ</th>
                        <th rowSpan={2} style={{ ...s.th, width: 90 }}>Số lượng nhận trong kỳ</th>
                        <th rowSpan={2} style={{ ...s.th, width: 90 }}>Tổng số</th>
                        <th colSpan={3} style={s.th}>Số hàng đã bán trong kỳ</th>
                        <th rowSpan={2} style={{ ...s.th, width: 110 }}>Số lượng tồn cuối kỳ</th>
                        {!containView ? <th rowSpan={2} style={{ ...s.th, width: 40 }}>#</th> : null}
                      </tr>
                      <tr>
                        <th style={s.th}>Số lượng</th>
                        <th style={s.th}>Đơn giá</th>
                        <th style={s.th}>Số tiền</th>
                      </tr>
                      <tr>
                        <th style={s.th}>A</th>
                        <th style={s.th}>B</th>
                        <th style={s.th}>C</th>
                        <th style={s.th}>1</th>
                        <th style={s.th}>2</th>
                        <th style={s.th}>3 = 1+2</th>
                        <th style={s.th}>4</th>
                        <th style={s.th}>5</th>
                        <th style={s.th}>6</th>
                        <th style={s.th}>7 = 3-4</th>
                        {!containView ? <th style={s.th} /> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => (
                        <tr key={`consignment-row-${index}`}>
                          <td style={{ ...s.td, textAlign: "center", fontWeight: 600 }}>{index + 1}</td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputLeft}
                              value={row.itemName}
                              onChange={(event) => setRowField(index, "itemName", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputCenter}
                              value={row.unit}
                              onChange={(event) => setRowField(index, "unit", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.quantityPrevious}
                              onChange={(event) => setRowField(index, "quantityPrevious", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.quantityReceived}
                              onChange={(event) => setRowField(index, "quantityReceived", event.target.value)}
                            />
                          </td>
                          <td style={{ ...s.td, textAlign: "right", padding: "0 6px" }}>
                            {formatQtyOutput(derivedRows[index]?.quantityTotal || 0)}
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.quantitySold}
                              onChange={(event) => setRowField(index, "quantitySold", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.unitPrice}
                              onChange={(event) => setRowField(index, "unitPrice", event.target.value)}
                            />
                          </td>
                          <td style={{ ...s.td, textAlign: "right", padding: "0 6px" }}>
                            {fmtMoney(derivedRows[index]?.amount || 0)}
                          </td>
                          <td style={{ ...s.td, textAlign: "right", padding: "0 6px" }}>
                            {formatQtyOutput(derivedRows[index]?.quantityRemaining || 0)}
                          </td>
                          {!containView ? (
                            <td style={{ ...s.td, textAlign: "center" }}>
                              <button
                                type="button"
                                onClick={() => removeRow(index)}
                                style={{ border: "none", background: "transparent", color: "#d32f2f", fontWeight: 700, cursor: "pointer" }}
                              >
                                x
                              </button>
                            </td>
                          ) : null}
                        </tr>
                      ))}

                      <tr>
                        <td colSpan={6} style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>Cộng</td>
                        <td style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>x</td>
                        <td style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>x</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{fmtMoney(totalAmount)}</td>
                        <td style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>x</td>
                        {!containView ? <td style={s.td} /> : null}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div style={{ marginTop: 12, lineHeight: 1.8 }}>
                  <div>
                    <b>II.</b> Số tiền còn nợ của các kỳ trước:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 160, textAlign: "right" }}
                      value={summary.previousDebt}
                      onChange={(event) => setSummaryField("previousDebt", event.target.value)}
                    />
                  </div>
                  <div>
                    <b>III.</b> Số tiền phải thanh toán đến kỳ này (III = II + Cột 6):{" "}
                    <span style={{ fontWeight: 700 }}>{fmtMoney(summaryNumbers.payableNew)}</span>
                  </div>
                  <div>
                    <b>IV.</b> Số tiền được nhận lại:
                    <span style={{ fontWeight: 700 }}> {fmtMoney(summaryNumbers.payableToConsignee)}</span>
                    <div>
                      + Hoa hồng:{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 140, textAlign: "right" }}
                        value={summary.commission}
                        onChange={(event) => setSummaryField("commission", event.target.value)}
                      />
                    </div>
                    <div>
                      + Thuế nộp hộ:{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 140, textAlign: "right" }}
                        value={summary.taxPaid}
                        onChange={(event) => setSummaryField("taxPaid", event.target.value)}
                      />
                    </div>
                    <div>
                      + Chi phí (nếu có):{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 140, textAlign: "right" }}
                        value={summary.otherExpense}
                        onChange={(event) => setSummaryField("otherExpense", event.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <b>V.</b> Số tiền thanh toán kỳ này:
                  </div>
                  <div>
                    + Tiền mặt:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 120, textAlign: "right" }}
                      value={summary.paidCash}
                      onChange={(event) => setSummaryField("paidCash", event.target.value)}
                    />
                  </div>
                  <div>
                    + Séc:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 120, textAlign: "right" }}
                      value={summary.paidCheque}
                      onChange={(event) => setSummaryField("paidCheque", event.target.value)}
                    />
                  </div>
                  <div>
                    <b>VI.</b> Số tiền nợ lại (VI = III - IV - V):{" "}
                    <span style={{ fontWeight: 700 }}>{fmtMoney(summaryNumbers.remainingDebt)}</span>
                  </div>
                  <div>
                    <b>* Ghi chú:</b>{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 260 }}
                      value={header.note}
                      onChange={(event) => setHeaderField("note", event.target.value)}
                    />
                  </div>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
                  <SoftButton variant="outlined" color="dark" onClick={() => navigation(-1)}>
                    Trở về
                  </SoftButton>

                  <div style={{ display: "flex", gap: 12 }}>
                    {!containView ? (
                      <SoftButton variant="outlined" color="dark" onClick={addRow}>
                        THÊM DÒNG
                      </SoftButton>
                    ) : null}

                    {containView ? (
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                        In Bảng
                      </SoftButton>
                    ) : null}

                    {!containView ? (
                      <SoftButton variant="gradient" color="dark" onClick={submit} disabled={loadingSubmit}>
                        {id ? "Lưu" : "Tạo"}
                      </SoftButton>
                    ) : null}
                  </div>
                </div>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
